import { DateTime } from 'luxon'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { BatchesTableRow } from '../../interfaces/ShipWiseBatchRecords.interfaces'
import { Button, Col, Container, Form, Row } from 'react-bootstrap'
import { Bar } from 'react-chartjs-2'
import { Chart, registerables } from 'chart.js'
import { CommonProperties } from '../../../common/common.interfaces'
import { useLoading } from '../../../loading/LoadingContext'
import { notify } from '../../../notify/notify'
import DesktopShipperErrorModalComponent from './ShipWiseErrorModalComponent'
import RefreshSvg from '../../../common/components/svg/RefreshSvg'
import BarChartSvg from '../../../common/components/svg/BarChartSvg'
import XSvg from '../../../common/components/svg/X-Svg'
import ShipWiseFilterComponent from './ShipWiseFilterComponent'
import { useUserContext } from '../../../user/UserContext'
import { apiFetch } from '../../../utility/fetch'

Chart.register(...registerables)

interface SortOptions<T> {
  field: string
  order: string
}

function sortByField<T>(array: T[], options: SortOptions<T>): T[] {
  const { field, order } = options
  
  const compare = (a: T, b: T): number => {
    const aValue = extractDateValue(a[field])
    const bValue = extractDateValue(b[field])
    
    if (aValue < bValue) {
      return order === 'asc' ? -1 : 1
    }
    
    if (aValue > bValue) {
      return order === 'asc' ? 1 : -1
    }
    
    return 0
  };
  
  return array.slice().sort(compare);
}

function extractDateValue(value: any): Date | any {
  if (typeof value === 'string' && isDateTimeString(value)) {
    const [month, day, year] = value.split('-');
    const formattedDate = `${month}-${day}-${year}`;
    return new Date(formattedDate);
  }
  
  return value;
}

function isDateTimeString(value: string): boolean {
  const dateTimeRegex = /^\d{2}-\d{2}-\d{4} \d{1,2}:\d{2} [AP]M$/i;
  const dateRegex = /^\d{2}-\d{2}-\d{4}$/i;

  return dateTimeRegex.test(value) || dateRegex.test(value);
}

const onlyUnique = (value, index, self) => self.indexOf(value) === index;

interface Dashboard {
  pendingLabelPrinted: number
  pendingBatchCreate: number
  pendingProcessing: number
  pendingRateAndShip: number
  inspectionRequired: number
  pendingPackToSuit: number
  aLocation: number
  bLocation: number
  cLocation: number
  aeLocation: number
  mhsLocation: number
  packToSuitLocation: number
}

const ShipWiseBatchRecordsComponent = () => {
  const { toggleLoading } = useLoading();
  const {userData} = useUserContext()

  const [lastRefresh, setLastRefresh] = useState(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
  const [rows, setRows]: [BatchesTableRow[], any] = useState(undefined)
  const [filters, setFilters] = useState([])
  const [filteredCount, setFilteredCount] = useState(0)
  const [dashboard, setDashboard]: [Dashboard, any] = useState(undefined)
  const [showChart, setShowChart] = useState(false)
  const [sortField, setSortField] = useState('displayShipDetailOrderTime')
  const [sortOrder, setSortOrder] = useState('desc')
  const [runModalVisible, setRunsModalVisible] = useState(false)
  const [errorModalVisible, setErrorModalVisible] = useState(false)
  const [errorRecord, setErrorRecord] = useState(undefined)
  const [salesforceUrl, setSalesforceUrl] = useState(undefined)

  /** Loading */
  useEffect(() => {
    const load = async () => {
      toggleLoading(true)
      try {
        setSalesforceUrl(window.location.href.includes('localhost') ? 'https://nethealthshopsllc--testing.sandbox.lightning.force.com' : 'https://nethealthshopsllc.lightning.force.com')

        /** Fill search by fields */
        const records: BatchesTableRow[] = []
        const response = await apiFetch<BatchesTableRow[]>({
          endpoint: 'azureTables',
          method: 'GET',
          accessToken: userData.accessToken,
          params: [{key: 'table', value: 'batches'}]
        })

        for(const record of response.body){
          record.batchProcess = !record.batchProcess ? 'Not Available' : record.batchProcess
          record.desktopShipperBatchId = !record.desktopShipperBatchId ? 'Not Available' : record.desktopShipperBatchId
          record.displayShipDetailOrderDate = DateTime.fromISO(record.shipDetailOrderTime).setZone('America/Chicago').toFormat('MM-dd-yyyy')
          record.displayShipDetailOrderTime = DateTime.fromISO(record.shipDetailOrderTime).setZone('America/Chicago').toFormat('MM-dd-yyyy t')
        }

        setRows(response.body)
      }catch(error){
        console.error(error)
      }
      toggleLoading(false)
    }
    load()
  }, [lastRefresh])

  const handleFilterChanged = useCallback((e) => {
    setFilters(prev => Array.from(new Set([...prev, e.currentTarget.id])));
  }, []);  

  const handleSortChanged = useCallback((e) => {
    setSortField(e.currentTarget.id)
    if(sortOrder === 'asc'){
      setSortOrder('desc')
    } else if(sortOrder === 'desc'){
      setSortOrder('asc')
    } else {
      setSortOrder('asc')
    }
  }, [sortOrder, sortField])

  const handleStatusSelectionChanged = useCallback((e) => {
    const updateOrder = async () => {
      if(typeof e === 'undefined') return

      const valueSplit = e.target.value.split('|')
      const pKey = valueSplit[0]
      const rKey = valueSplit[1]
      const newStatus = valueSplit[2]
      const row: BatchesTableRow = rows.find(r => r.partitionKey === pKey && r.rowKey === rKey)
      if(confirm(`Are you sure you wish to update order [${row.shipDetailOrderNumber} / ${row.destktopShipperOrderId}] status to [${newStatus}]?`)){
        toggleLoading(true)
        const response = await apiFetch({
          endpoint: 'azureTables',
          method: 'POST',
          accessToken: userData.accessToken,
          body: JSON.stringify({table: 'batches', records: [{...row, status: newStatus}]})
        })
        if(!response.ok){
          notify('error', `${response.body}`)
        } else {
          notify('success', `Order [${row.destktopShipperOrderId} / ${row.shipDetailOrderNumber}] status updated to [${newStatus}]`)
          setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))
        }
        toggleLoading(false)
      } else {
        e.target.value = `${pKey}|${rKey}|${row.status}`
      }
    }
    updateOrder()
  }, [rows])

  const handleErrorClicked = useCallback((e) => {
    if(typeof e === 'undefined') return
    const valueSplit = e.target.id.split('|')
    const pKey = valueSplit[0]
    const rKey = valueSplit[1]
    setErrorRecord(rows.find(r => r.partitionKey === pKey && r.rowKey === rKey))
    setErrorModalVisible(true)
  }, [rows])

  const buildTable = useMemo(() => {
    if(typeof rows === 'undefined') return

    const filteredRows = rows.filter(item =>
      filters?.every(filter => {
        const [key, value] = filter.split('|');
        return `${item[key]}` === `${value}`;
      })
    );

    let sortedRows = filteredRows
    if(typeof sortField !== 'undefined' && typeof sortOrder !== 'undefined'){
      sortedRows = sortByField(filteredRows, {field: sortField, order: sortOrder})
    }

    const statuses = filteredRows.map(r => r.status)
    const pendingBatchCreates = statuses.filter(r => r === 'pending-batch-create')
    const pendingProcessings = statuses.filter(r => r === 'pending-processing')
    const pendingRateAndShips = statuses.filter(r => r === 'pending-rateAndShip')
    const pendingLabelPrinteds = statuses.filter(r => r === 'pending-label-printed')
    const inspectionRequireds = statuses.filter(r => r === 'inspection-required')
    const pendingPackToSuit = statuses.filter(r => r === 'pending-packToSuit')

    const batchNames = filteredRows.map(r => r.batchName)
    const aLocations = batchNames.filter(r => r === 'A-Warehouse-Conveyable')
    const bLocations = batchNames.filter(r => r === 'B-Warehouse-Conveyable')
    const cLocations = batchNames.filter(r => r === 'C-Warehouse-Conveyable')
    const aeLocations = batchNames.filter(r => r === 'AE-NonConveyable')
    const mhsLocations = batchNames.filter(r => r === 'MHS-Stands')
    const packToSuitLocations = batchNames.filter(r => r === 'PackToSuit')

    const uniqueOrderNumbers = filteredRows.map(r => `${r.shipDetailOrderNumber}`).filter(onlyUnique)
    const uniqueDSOrderIds = filteredRows.map(r => `${r.destktopShipperOrderId}`).filter(onlyUnique)
    const uniqueStatuses = statuses.filter(onlyUnique)
    const uniqueLocations = filteredRows.map(r => r.warehouseLocation).filter(onlyUnique)
    const uniqueBatchProcesses = filteredRows.map(r => r.batchProcess).filter(onlyUnique)
    const uniqueBatchNames = batchNames.filter(onlyUnique)
    const uniqueBatchIds = filteredRows.map(r => r.desktopShipperBatchId).filter(onlyUnique)
    const uniqueDays = filteredRows.map(r => r.displayShipDetailOrderTime).filter(onlyUnique)
    const uniqueDates = filteredRows.map(r => r.displayShipDetailOrderDate).filter(onlyUnique)
    const uniqueMpns = filteredRows.map(r => r.mpn).filter(onlyUnique)

    const sortText = typeof sortField === 'undefined' ? '' : typeof sortOrder === 'undefined' ? '' : sortOrder === 'asc' ? 'ASC' : 'DESC'
    const th = []
    th.push(
      <td key={`header_orderNumber`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueOrderNumbers}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='shipDetailOrderNumber'
          title='Order #'
        />
      </td>
    )
    th.push(
      <td key={`header_shipwiseid`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueDSOrderIds}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='destktopShipperOrderId'
          title='ShipWise ID'
        />
      </td>
    )
    th.push(
      <td key={`header_status`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueStatuses}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='status'
          title='Status'
        />
      </td>
    )
    th.push(
      <td key={`header_sdot`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueDays}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='displayShipDetailOrderTime'
          title='Order Time'
        />
      </td>
    )
    th.push(
      <td key={`header_date`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueDates}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='displayShipDetailOrderDate'
          title='Order Date'
        />
      </td>
    )
    th.push(
      <td key={`header_mpn`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueMpns}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='mpn'
          title='SKU'
        />
      </td>
    )
    th.push(
      <td key={`header_wl`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueLocations}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='warehouseLocation'
          title='WH Location'
        />
      </td>
    )
    th.push(
      <td key={`header_bn`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueBatchNames}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='batchName'
          title='Batch'
        />
      </td>
    )
    th.push(
      <td key={`header_bp`}>
        <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueBatchProcesses}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='batchProcess'
          title='Process'
        />        
      </td>
    )
    th.push(
    <td key={`header_dsbi`}>
      <ShipWiseFilterComponent 
          currentSortByField={sortField}
          currentSortByText={sortText}
          items={uniqueBatchIds}
          onFilterChanged={handleFilterChanged}
          onSortChanged={handleSortChanged}
          sortByField='desktopShipperBatchId'
          title='Batch ID'
        />   
      </td>
    )
    th.push(<td key={`header_url`}><span className='shipwise-title'>Salesforce<br/></span><br/></td>)
    th.push(<td key={'error'}><span className='shipwise-title'>Error<br/></span><br/></td>)

    const tr = []
    for(const row of sortedRows){
      let statusSelect
      const options = []
      for(const status of ['pending-processing', 'pending-batch-create', 'pending-rateAndShip', 'pending-label-printed', 'inspection-required', 'pending-packToSuit']){
        options.push(<option id={`${row.partitionKey}|${row.rowKey}|${status}`} key={`${row.partitionKey}|${row.rowKey}|${status}`} value={`${row.partitionKey}|${row.rowKey}|${status}`}>{status.toUpperCase()}</option>)
      }
      statusSelect = (
        <Form.Select defaultValue={`${row.partitionKey}|${row.rowKey}|${row.status}`} key={`statusselect_${row.rowKey}`} onChange={handleStatusSelectionChanged}>
          {options}
        </Form.Select>
      )
      const td = []
      td.push(<td key={`sdon_${row.rowKey}`} style={{fontWeight: sortField === 'shipDetailOrderNumber' ? 'bolder' : 'lighter'}}>{row.shipDetailOrderNumber}</td>)
      td.push(<td key={`dsoi_${row.rowKey}`} style={{fontWeight: sortField === 'destktopShipperOrderId' ? 'bolder' : 'lighter'}}>{row.destktopShipperOrderId}</td>)
      td.push(<td key={`status_${row.rowKey}`} style={{fontWeight: sortField === 'status' ? 'bolder' : 'lighter'}}>{statusSelect}</td>)
      td.push(<td key={`sdot_${row.rowKey}`} style={{fontWeight: sortField === 'displayShipDetailOrderTime' ? 'bolder' : 'lighter'}}>{row.displayShipDetailOrderTime}</td>)
      td.push(<td key={`date_${row.rowKey}`} style={{fontWeight: sortField === 'displayShipDetailOrderDate' ? 'bolder' : 'lighter'}}>{row.displayShipDetailOrderDate}</td>)
      td.push(<td key={`mpn_${row.rowKey}`} style={{fontWeight: sortField === 'mpn' ? 'bolder' : 'lighter'}}>{row.mpn}</td>)
      td.push(<td key={`wl_${row.rowKey}`} style={{fontWeight: sortField === 'warehouseLocation' ? 'bolder' : 'lighter'}}>{row.warehouseLocation}</td>)
      td.push(<td key={`bn_${row.rowKey}`} style={{fontWeight: sortField === 'batchName' ? 'bolder' : 'lighter'}}>{row.batchName}</td>)
      td.push(<td key={`bp_${row.rowKey}`} style={{fontWeight: sortField === 'batchProcess' ? 'bolder' : 'lighter'}}>{row.batchProcess ?? 'No Batch Yet'}</td>)
      td.push(<td key={`dsbi_${row.rowKey}`} style={{fontWeight: sortField === 'desktopShipperBatchId' ? 'bolder' : 'lighter'}}>{row.desktopShipperBatchId}</td>)
      td.push(<td key={`url_${row.rowKey}`}><a className='beacon-link' target="_blank" rel="noopener noreferrer" href={`${salesforceUrl}/${row.shipDetailId}`}>View</a></td>)
      td.push(<td key={`error_${row.rowKey}`}>
          {row.status === 'inspection-required' ? <Button id={`${row.partitionKey}|${row.rowKey}|${row.status}`} onClick={handleErrorClicked} variant='secondary'>Error</Button> : ''}
      </td>)
      tr.push(<tr className='shipwise-row' key={`${row.rowKey}`}>{td}</tr>)
    }

    setFilteredCount(tr.length)
    const dashboard: Dashboard = {
      pendingBatchCreate: pendingBatchCreates.length,
      pendingLabelPrinted: pendingLabelPrinteds.length,
      pendingProcessing: pendingProcessings.length,
      pendingRateAndShip: pendingRateAndShips.length,
      inspectionRequired: inspectionRequireds.length,
      pendingPackToSuit: pendingPackToSuit.length,
      aeLocation: aeLocations.length,
      aLocation: aLocations.length,
      bLocation: bLocations.length,
      cLocation: cLocations.length,
      mhsLocation: mhsLocations.length,
      packToSuitLocation: packToSuitLocations.length
    }
    setDashboard(dashboard)

    return (
      <>        
        <table className='shipwise-table'>
          <thead>
            <tr className='sticky-row'>{th}</tr>
          </thead>
          <tbody>
            {tr}
          </tbody>
        </table>
      </>
    )

  }, [rows, filters, handleFilterChanged, sortField, sortOrder, salesforceUrl])

  const handleFilterRemoved = useCallback((e) => {
    const newFilters = filters.filter(filter => !filter.includes(e.currentTarget.id));
    setFilters(newFilters);
  }, [filters]);

  const filterItems = useMemo(() => {
    return filters.map(filter => {
      const value = filter.split('|')[1].toUpperCase();
      return (
        <div
          key={filter}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            background: '#f0f0f0',
            borderRadius: '20px',
            padding: '5px 10px',
            margin: '4px',
            fontSize: '0.85rem',
            fontWeight: 'bold',
            color: '#333',
          }}
        >
          <span
            id={filter}
            onClick={handleFilterRemoved}
            style={{
              cursor: 'pointer',
              marginRight: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <XSvg height={12} />
          </span>
          {value}
        </div>
      );
    });
  }, [filters, handleFilterRemoved]);  

  const buildDashboard = useMemo(() => {
    if(typeof dashboard === 'undefined') return    

    return (
      <div>
        <Container>
          <Row>
            <Col style={{height: '250px'}}>
            <Bar data={{          
              labels: [`Pending Rate & Ship [${dashboard.pendingRateAndShip}]`, `Pending Processing [${dashboard.pendingProcessing}]`, `Pending Batch Create [${dashboard.pendingBatchCreate}]`, `Pending Label Printed [${dashboard.pendingLabelPrinted}]`, `Inspection Required [${dashboard.inspectionRequired}]`, `Pending Pack To Suit [${dashboard.pendingPackToSuit}]`],
              datasets: [{
                label: 'Status',
                data: [dashboard.pendingRateAndShip, dashboard.pendingProcessing, dashboard.pendingBatchCreate, dashboard.pendingLabelPrinted, dashboard.inspectionRequired, dashboard.pendingPackToSuit],
                backgroundColor: [
                  'rgba(255, 99, 132, 0.2)',
                  'rgba(255, 159, 64, 0.2)',
                  'rgba(255, 205, 86, 0.2)',
                  'rgba(75, 192, 192, 0.2)',
                  'rgba(54, 162, 235, 0.2)',
                  'rgba(153, 102, 255, 0.2)',
                  'rgba(201, 203, 207, 0.2)'
                ],
                borderColor: [
                  'rgb(255, 99, 132)',
                  'rgb(255, 159, 64)',
                  'rgb(255, 205, 86)',
                  'rgb(75, 192, 192)',
                  'rgb(54, 162, 235)',
                  'rgb(153, 102, 255)',
                  'rgb(201, 203, 207)'
                ],
                borderWidth: 1
              }]
            }} options={{
              scales: {
                y: {
                  beginAtZero: true
                }
              }
            }}></Bar>
            </Col>
            <Col style={{height: '250px'}}>
            <Bar data={{          
              labels: ['A-Warehouse-Conveyable', 'B-Warehouse-Conveyable', 'C-Warehouse-Conveyable', 'AE-NonConveyable', 'MHS-Stands', 'PackToSuit'],
              datasets: [{
                label: 'Batch Type',
                data: [dashboard.aLocation, dashboard.bLocation, dashboard.cLocation, dashboard.aeLocation, dashboard.mhsLocation, dashboard.packToSuitLocation],
                backgroundColor: [
                  'rgba(255, 99, 132, 0.2)',
                  'rgba(255, 159, 64, 0.2)',
                  'rgba(255, 205, 86, 0.2)',
                  'rgba(75, 192, 192, 0.2)',
                  'rgba(54, 162, 235, 0.2)',
                  'rgba(153, 102, 255, 0.2)',
                  'rgba(201, 203, 207, 0.2)'
                ],
                borderColor: [
                  'rgb(255, 99, 132)',
                  'rgb(255, 159, 64)',
                  'rgb(255, 205, 86)',
                  'rgb(75, 192, 192)',
                  'rgb(54, 162, 235)',
                  'rgb(153, 102, 255)',
                  'rgb(201, 203, 207)'
                ],
                borderWidth: 1
              }]
            }} options={{
              scales: {
                y: {
                  beginAtZero: true
                }
              }
            }}></Bar>
            </Col>
          </Row>
        </Container>
       
        
      </div>
    )
  }, [dashboard])

  const closeRunsModal = useCallback((e) => {
    setRunsModalVisible(false)
  }, [])

  const closeErrorModal = useCallback((e) => {
    setErrorModalVisible(false)
    setErrorRecord(undefined)
  }, [])

  const display = useMemo(() => {
    return (
      <div>
        <DesktopShipperErrorModalComponent salesforceUrl={salesforceUrl} closeModal={closeErrorModal} modalVisible={errorModalVisible} record={errorRecord}></DesktopShipperErrorModalComponent>
        <div id="top"></div>
        <span 
          title='Refresh orders' 
          style={{cursor: 'pointer'}} 
          onClick={()=> {setLastRefresh(DateTime.now().toLocaleString(DateTime.TIME_WITH_SECONDS))}}>
            <RefreshSvg height={25}/>
        </span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span className='beacon-label'>
          as of {lastRefresh}
        </span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span 
          title='See bar charts related to the data' 
          style={{cursor: 'pointer'}} 
          onClick={() => {setShowChart(!showChart)}}
        >
          <BarChartSvg height={25} style={showChart ? 'stylish' : 'black-white'} />
          <span className='beacon-label'>{showChart ? 'Hide' : 'Show'}</span>
        </span>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <span 
          title='Clear all filters' 
          style={{cursor: 'pointer'}} 
          onClick={() => {setFilters([])}}
        >
          <XSvg height={25} />
          <span className='beacon-label'>CLEAR FILTER</span>
        </span>
        {showChart && buildDashboard} 
        <hr/>
        {filterItems}
        <br/>
        <span className='beacon-label'>{filteredCount.toLocaleString()}/{rows?.length} row(s). Click a header to sort the table.</span>
        <hr/>
        {buildTable}
      </div>
    )
  }, [
    salesforceUrl, 
    closeErrorModal, 
    errorModalVisible, 
    errorRecord, 
    closeRunsModal, 
    runModalVisible, 
    showChart, 
    buildDashboard, 
    setRunsModalVisible, 
    setShowChart, 
    setFilters, 
    lastRefresh, 
    filteredCount, 
    filterItems, 
    buildTable
  ])

  
  return display
}

export default ShipWiseBatchRecordsComponent